import type { Content, Editor } from '@tiptap/core'
import type { Transaction } from '@tiptap/pm/state'

// Marks a whole-document replacement that replays persisted or remote state
// rather than anything the clinician did. Distinct from `addToHistory: false`,
// which several genuine local edits also carry (an AI stamp writing its own
// node attrs, for one) — this key means specifically "the document was replaced
// by a machine sync".
const MACHINE_CONTENT_SYNC_META = 'seenMachineContentSync'

// Options passed straight through to Tiptap's setContent for a machine sync.
interface MachineContentSyncOptions {
  emitUpdate?: boolean
  parseOptions?: { preserveWhitespace?: boolean | 'full' }
}

// Replace the whole editor document with a machine-derived content sync — a
// template seed, or a Medplum->Liveblocks reconcile — WITHOUT recording it on any
// undo stack. These resets replay persisted/remote state the clinician never
// typed, so a later Cmd+Z must not be able to pop one. y-prosemirror's UndoManager
// skips a transaction whose `addToHistory` meta is false (its
// captureTransaction is `tr.meta.get('addToHistory') !== false`), and the
// ProseMirror history plugin honors the same meta. Popping an un-suppressed
// mount-time reconcile is exactly what let a post-reload undo misfire the
// accepted->pending reconciler and clear a committed accept's receipt server-side.
export function setContentWithoutHistory(
  editor: Editor,
  content: Content,
  options?: MachineContentSyncOptions
): void {
  editor
    .chain()
    .setContent(content, options)
    .setMeta('addToHistory', false)
    .setMeta(MACHINE_CONTENT_SYNC_META, true)
    .run()
}

// True when this transaction is one of the above replacements. The save gate
// reads it: a machine sync writes back content the server already holds, so
// persisting it buys nothing — and when the sync is the WRONG one (a reconcile
// judged against a cache entry that predates this client's own write), saving
// it turns recoverable on-screen staleness into a persisted overwrite.
export function isMachineContentSync(tr: Transaction): boolean {
  return tr.getMeta(MACHINE_CONTENT_SYNC_META) === true
}
